import { deepCloneObject } from '@/utils/objectManipulation';
import { Ajv2020 } from 'ajv/dist/2020';
import type { DataValidateFunction } from 'ajv/dist/types';

export function addCustomValidators(ajvInstance: Ajv2020) {
  ajvInstance.addKeyword({
    keyword: 'sumEqual',
    type: 'object',
    errors: true,
    compile(expectedFields) {
      return function validate(data) {
        const total = Number(data[expectedFields.total]);
        const sumFields = expectedFields.sumFields.map((field: string) => Number(data[field]));
        const sum = sumFields.reduce((acc: number, curr: number) => acc + curr, 0);
        if (isNaN(total) || isNaN(sum)) {
          return true;
        }
        const isValid = total === sum;
        if (!isValid) {
          (validate as DataValidateFunction).errors = [{
            keyword: 'sumEqual'
          }];
        }
        return isValid;
      };
    }
  });
  ajvInstance.addKeyword({
    keyword: 'fourDigitNumber',
    type: ['number', 'null'],
    errors: true,
    compile(allowNull: boolean) {
      return function validate(data) {
        if (data === null && allowNull) {
          return false;
        }
        const isValid = data.toString().length === 4;
        if (!isValid) {
          (validate as DataValidateFunction).errors = [{
            keyword: 'fourDigitNumber'
          }];
        }
        return isValid;
      };
    }
  });

  ajvInstance.addKeyword({
    keyword: 'greaterThanField',
    type: 'object',
    errors: true,
    compile(compareFields: { greater: string; less: string }) {
      return function validate(data) {
        if (data[compareFields.greater] === null || data[compareFields.less] === null) {
          return false;
        }
        const greater = Number(data[compareFields.greater]);
        const less = Number(data[compareFields.less]);
        const isValid = greater > less;
        if (!isValid) {
          (validate as DataValidateFunction).errors = [{
            keyword: 'greaterThanField'
          }];
        }
        return isValid;
      };
    }
  });

  ajvInstance.addKeyword({
    keyword: 'hasDuplicateKeys',
    type: 'array',
    errors: true,
    compile({ path, exceptionValues = [] }: { path: string; exceptionValues?: (string | number)[] }) {
      return function validate(data) {
        const paths = path.split('/');
        const uniqueIds = new Set();
        const visitedIds = new Set();
        let exceptionCount = 0;
        data.forEach((item: any) => {
          let row = deepCloneObject(item);
          paths.forEach((path, idx) => {
            if (row === undefined) {
              return;
            }
            row = row[path];
            if (idx === paths.length - 1) {
              if (exceptionValues.includes(row)) {
                exceptionCount++;
                return;
              }
              if (uniqueIds.has(row)) {
                visitedIds.add(row);
              } else {
                uniqueIds.add(row);
              }
            }
          });
        });
        if (!uniqueIds.size) {
          return true;
        }
        const isValid = uniqueIds.size === data.length - exceptionCount;
        if (!isValid) {
          const duplicateArray = Array.from(visitedIds);
          if (!duplicateArray.length) {
            return true;
          }

          (validate as DataValidateFunction).errors = [{
            keyword: 'hasDuplicateKeys',
            params: { duplicates: duplicateArray.join(', ') }
          }];
        }
        return isValid;
      };
    }
  });
}
